// A read receipt means the message was on screen in a focused, visible window
// continuously for 800 ms. Opening a channel or scrolling past is insufficient.
export function observeFocusedMessage(element: HTMLElement, onViewed: () => Promise<unknown>, onVisible: (visible: boolean) => void = () => { /* Optional visibility subscriber. */ }) {
  let visible = false;
  let stopped = false;
  let recorded = false;
  let pending = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const clear = () => { if (timer !== undefined) clearTimeout(timer); timer = undefined; };
  const hasModal = () => document.querySelector('[aria-modal="true"], dialog[open]') !== null;
  let modalOpen = hasModal();
  const eligible = () => visible && document.visibilityState === "visible" && document.hasFocus() && !hasModal();
  const check = () => {
    clear();
    if (stopped || recorded || pending || !eligible()) return;
    timer = setTimeout(() => {
      timer = undefined;
      if (stopped || !eligible()) return;
      pending = true;
      void onViewed().then(() => { recorded = true; }).catch(() => { /* Retry only on a later focus/visibility transition. */ }).finally(() => { pending = false; });
    }, 800);
  };
  if (typeof IntersectionObserver === "undefined") return () => { /* No observer to dispose. */ };
  const observer = new IntersectionObserver(entries => {
    const entry = entries.find(item => item.target === element);
    if (!entry) return;
    visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
    onVisible(visible);
    check();
  }, { threshold: [0, 0.5, 1] });
  observer.observe(element);
  const modalObserver = typeof MutationObserver === "undefined" ? null : new MutationObserver(() => {
    const current = hasModal();
    if (current !== modalOpen) { modalOpen = current; check(); }
  });
  modalObserver?.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["aria-modal", "open"] });
  document.addEventListener("visibilitychange", check);
  window.addEventListener("focus", check);
  window.addEventListener("blur", check);
  return () => {
    stopped = true; clear(); observer.disconnect(); modalObserver?.disconnect();
    document.removeEventListener("visibilitychange", check);
    window.removeEventListener("focus", check); window.removeEventListener("blur", check);
  };
}
